import React from 'react';

import { ActionIcon, Button, Flex, FlexProps, ScrollArea, Title } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface ManageSmartAssistantControlWrapperProps extends FlexProps {
  title: string;
  hasChanges?: boolean;
  loading?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  onBack?: () => void;
  disabled?: boolean;
  withMarginBottom?: boolean;
  extraRightSection?: React.ReactNode;
  children: React.ReactNode;
}

export function ManageSmartAssistantSectionControlWrapper({
  title,
  hasChanges = false,
  loading = false,
  onSave,
  onCancel,
  onBack,
  disabled = false,
  withMarginBottom = false,
  extraRightSection,
  children,
  ...flexProps
}: ManageSmartAssistantControlWrapperProps) {
  const { t } = useTranslation();

  const headerMarginBottom = withMarginBottom ? 16 : 12;

  const renderTitle = () => {
    if (onBack) {
      return (
        <Flex align="center" gap={8} onClick={onBack} sx={{ cursor: 'pointer' }}>
          <ActionIcon color="dark" variant="transparent" size="sm">
            <IconArrowLeft size={16} />
          </ActionIcon>
          <Title order={2} sx={{ lineHeight: 1 }}>
            {title}
          </Title>
        </Flex>
      );
    }

    return (
      <Title order={2} sx={{ lineHeight: 1 }}>
        {title}
      </Title>
    );
  };

  const renderActionButtons = () => {
    if (!hasChanges) return null;

    return (
      <>
        <Button variant="outline" size="sm" onClick={onCancel} disabled={loading} h={32} px={16}>
          {t('APP.ACTIONS.CANCEL')}
        </Button>
        <Button
          variant="filled"
          size="sm"
          loading={loading}
          onClick={onSave}
          disabled={disabled}
          h={32}
          px={16}
          bg="dark.8">
          {t('APP.ACTIONS.SAVE')}
        </Button>
      </>
    );
  };

  return (
    <Flex h="100%" direction="column" {...flexProps}>
      <Flex align="center" justify="space-between" w="100%" mb={headerMarginBottom} px={20} py={8}>
        {renderTitle()}

        <Flex align="center" gap={8} h={32}>
          {extraRightSection}
          {renderActionButtons()}
        </Flex>
      </Flex>

      <ScrollArea h="100%" px={20}>
        {children}
      </ScrollArea>
    </Flex>
  );
}
